import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getAllPhoneNumbers, createPhoneNumber, createBulkPhoneNumbers, updatePhoneNumber, updatePhoneNumberBlockStatus } from '../../services/SmsService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Pagination from '../../utils/Pagination';
import ExportButton from '../ExportButton';

// Custom debounce hook
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

function PhoneNumbers() {
  const { customerId } = useParams();
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newPhoneNumberModalOpen, setNewPhoneNumberModalOpen] = useState(false);
  const [bulkPhoneNumbersModalOpen, setBulkPhoneNumbersModalOpen] = useState(false);
  const [editPhoneNumberModalOpen, setEditPhoneNumberModalOpen] = useState(false);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState(null);
  const [showCreateDropdown, setShowCreateDropdown] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const fileInputRef = useRef(null);
  
  // New phone number form state
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [newFullName, setNewFullName] = useState('');
  const [newMetadata, setNewMetadata] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  
  // Bulk phone numbers form state
  const [bulkPhoneNumbersList, setBulkPhoneNumbersList] = useState('');
  const [csvPreviewData, setCsvPreviewData] = useState([]);
  const [csvPreviewModalOpen, setCsvPreviewModalOpen] = useState(false);
  const [isSavingBulk, setIsSavingBulk] = useState(false);
  
  // Edit phone number form state
  const [editPhoneNumber, setEditPhoneNumber] = useState('');
  const [editFullName, setEditFullName] = useState('');
  const [editMetadata, setEditMetadata] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  
  // Search and sort state
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDescending, setSortDescending] = useState(true);

  // Filter sidebar state
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState({
    phoneNumbers: '',
    fullName: '',
    metadata: '',
    status: '',
    blockReason: '',
    createdFrom: '',
    createdTo: '',
  });
  const [appliedFilters, setAppliedFilters] = useState({
    phoneNumbers: '',
    fullName: '',
    metadata: '',
    status: '',
    blockReason: '',
    createdFrom: '',
    createdTo: '',
  });

  // Sync tempFilters when sidebar opens
  useEffect(() => {
    setTempFilters({ ...appliedFilters });
  }, [isFilterOpen, appliedFilters]);

  // Count active filters
  const activeFilterCount = Object.values(appliedFilters).filter(v => v !== '').length;

  // Debounce the search term
  const debouncedSearchTerm = useDebounce(searchTerm, 2000);

  // Fetch phone numbers with pagination, search, sort, and filters
  const fetchPhoneNumbers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        pageNumber: currentPage,
        pageSize,
        searchTerm: debouncedSearchTerm,
        sortBy,
        sortDescending,
      };

      if (appliedFilters.phoneNumbers) params.phoneNumbers = appliedFilters.phoneNumbers;
      if (appliedFilters.fullName) params.fullName = appliedFilters.fullName;
      if (appliedFilters.metadata) params.metadata = appliedFilters.metadata;
      if (appliedFilters.status) params.status = appliedFilters.status;
      if (appliedFilters.blockReason) params.blockReason = appliedFilters.blockReason;
      if (appliedFilters.createdFrom) params.createdFrom = appliedFilters.createdFrom;
      if (appliedFilters.createdTo) params.createdTo = appliedFilters.createdTo;

      const data = await getAllPhoneNumbers(params);
      setPhoneNumbers(data.items || []);
      setTotalItems(data.totalCount || 0);
    } catch (err) {
      setError('Failed to load phone numbers');
      setPhoneNumbers([]);
      setTotalItems(0);
    }
    setLoading(false);
  }, [currentPage, pageSize, debouncedSearchTerm, sortBy, sortDescending, appliedFilters]);

  useEffect(() => {
    fetchPhoneNumbers();
  }, [fetchPhoneNumbers]);

  // Filter handlers
  const handleTempFilterChange = (e) => {
    const { name, value } = e.target;
    setTempFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...tempFilters });
    setCurrentPage(1);
    setIsFilterOpen(false);
  };

  const handleClearAllFilters = () => {
    const emptyFilters = {
      phoneNumbers: '',
      fullName: '',
      metadata: '',
      status: '',
      blockReason: '',
      createdFrom: '',
      createdTo: '',
    };
    setTempFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDescending(!sortDescending);
    } else {
      setSortBy(column);
      setSortDescending(true);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalItems / pageSize);

  const handleExport = async (format) => {
    setIsExporting(true);
    try {
      const { exportPhoneNumbers } = await import('../../services/SmsService');
      const params = {
        sortBy,
        sortDescending,
        exportTo: format,
      };
      if (appliedFilters.phoneNumbers) params.phoneNumbers = appliedFilters.phoneNumbers;
      if (appliedFilters.fullName) params.fullName = appliedFilters.fullName;
      if (appliedFilters.metadata) params.metadata = appliedFilters.metadata;
      if (appliedFilters.status) params.status = appliedFilters.status;
      if (appliedFilters.blockReason) params.blockReason = appliedFilters.blockReason;
      if (appliedFilters.createdFrom) params.createdFrom = appliedFilters.createdFrom;
      if (appliedFilters.createdTo) params.createdTo = appliedFilters.createdTo;

      const response = await exportPhoneNumbers(params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `phone-numbers.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to export phone numbers");
    } finally {
      setIsExporting(false);
    }
  };

  const handleNewPhoneNumberSubmit = async (e) => {
    e.preventDefault();
    
    if (!newPhoneNumber.trim()) {
      toast.error('Please enter a phone number');
      return;
    }

    setIsCreating(true);
    setCreateError(null);

    try {
      await createPhoneNumber({
        phoneNumber: newPhoneNumber.trim(),
        fullName: newFullName.trim() || null,
        metadata: newMetadata.trim() || null
      });
      toast.success('Phone number created successfully!');
      setNewPhoneNumberModalOpen(false);
      setNewPhoneNumber('');
      setNewFullName('');
      setNewMetadata('');
      fetchPhoneNumbers();
    } catch (error) {
      setCreateError('Failed to create phone number. Please try again.');
      toast.error('Failed to create phone number');
    } finally {
      setIsCreating(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const csvData = event.target.result;
        const lines = csvData.split('\n');
        const phoneNumbersListParsed = lines
          .filter(line => line.trim())
          .map(line => {
            const [fullName, phoneNumber, metadata] = line.split(',').map(item => item.trim());
            return {
              phoneNumber,
              fullName: fullName || '',
              metadata: metadata || ''
            };
          });

        setCsvPreviewData(phoneNumbersListParsed);
        setCsvPreviewModalOpen(true);
      } catch (error) {
        toast.error('Failed to parse CSV file');
      }
    };
    reader.readAsText(file);
  };

  const handleCsvDataChange = (index, field, value) => {
    const newData = [...csvPreviewData];
    newData[index] = {
      ...newData[index],
      [field]: value
    };
    setCsvPreviewData(newData);
  };

  const handleSaveBulkPhoneNumbers = async () => {
    setIsSavingBulk(true);
    try {
      const phoneNumbersListPayload = csvPreviewData.map(item => ({
        phoneNumber: item.phoneNumber,
        fullName: item.fullName || null,
        metadata: item.metadata || null
      }));

      await createBulkPhoneNumbers(phoneNumbersListPayload);
      toast.success(`${csvPreviewData.length} phone numbers created successfully!`);
      setCsvPreviewModalOpen(false);
      setCsvPreviewData([]);
      fetchPhoneNumbers();
    } catch (error) {
      toast.error('Failed to create phone numbers');
    } finally {
      setIsSavingBulk(false);
    }
  };

  const handleRemoveRow = (index) => {
    setCsvPreviewData(csvPreviewData.filter((_, i) => i !== index));
  };

  const handleBulkPhoneNumbersSubmit = async (e) => {
    e.preventDefault();
    
    if (!bulkPhoneNumbersList.trim()) {
      toast.error('Please enter phone numbers');
      return;
    }

    try {
      const phoneNumbersListPayload = bulkPhoneNumbersList
        .split('\n')
        .map(line => line.trim())
        .filter(line => line)
        .map(phoneNumber => ({
          phoneNumber,
          fullName: null,
          metadata: null
        }));

      await createBulkPhoneNumbers(phoneNumbersListPayload);
      toast.success(`${phoneNumbersListPayload.length} phone numbers created successfully!`);
      setBulkPhoneNumbersModalOpen(false);
      setBulkPhoneNumbersList('');
      fetchPhoneNumbers();
    } catch (error) {
      toast.error('Failed to create phone numbers');
    }
  };

  const handleEditPhoneNumber = (phoneNumber) => {
    setSelectedPhoneNumber(phoneNumber);
    setEditPhoneNumber(phoneNumber.number);
    setEditFullName(phoneNumber.fullName || '');
    setEditMetadata(phoneNumber.metadata || '');
    setEditPhoneNumberModalOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!editPhoneNumber.trim()) {
      toast.error('Please enter a phone number');
      return;
    }

    setIsUpdating(true);
    setUpdateError(null);

    try {
      await updatePhoneNumber({
        id: selectedPhoneNumber.id,
        phoneNumber: editPhoneNumber.trim(),
        fullName: editFullName.trim() || null,
        metadata: editMetadata.trim() || null
      });
      toast.success('Phone number updated successfully!');
      setEditPhoneNumberModalOpen(false);
      setSelectedPhoneNumber(null);
      fetchPhoneNumbers();
    } catch (error) {
      setUpdateError('Failed to update phone number. Please try again.');
      toast.error('Failed to update phone number');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleBlockStatusChange = async (phoneNumber, isBlocked) => {
    try {
      await updatePhoneNumberBlockStatus({
        phoneNumberId: phoneNumber.id,
        isBlocked: !isBlocked,
        blockReason: isBlocked ? null : 'Manually blocked by user'
      });
      toast.success(`Phone number ${isBlocked ? 'unblocked' : 'blocked'} successfully!`);
      fetchPhoneNumbers();
    } catch (error) {
      toast.error(`Failed to ${isBlocked ? 'unblock' : 'block'} phone number`);
    }
  };

  return (
    <div style={{ paddingLeft: 25, paddingRight: 25, width: '100%' }} className="mt-4">
      <ToastContainer />

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">Phone Numbers</h4>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setIsFilterOpen(true)}
            disabled={loading}
          >
            <i className="bi bi-funnel-fill me-1"></i>
            Filter
            {activeFilterCount > 0 && (
              <span className="badge bg-primary ms-1">{activeFilterCount}</span>
            )}
          </button>
          <ExportButton onExport={handleExport} isExporting={isExporting} size="sm" />
          <button
            className="btn btn-outline-secondary d-flex align-items-center"
            onClick={fetchPhoneNumbers}
            disabled={loading}
            title="Refresh"
          >
            {loading ? (
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            ) : (
              <i className="bi bi-arrow-clockwise me-2"></i>
            )}
            Refresh
          </button>
        </div>
      </div>

      {/* ==================== FILTER SIDEBAR ==================== */}
      <div
        className={`position-fixed top-0 end-0 bottom-0 start-0 bg-dark bg-opacity-50 ${!isFilterOpen ? "d-none" : ""}`}
        onClick={() => setIsFilterOpen(false)}
        style={{ zIndex: 1040 }}
      />
      <div
        className={`position-fixed top-0 end-0 bottom-0 bg-white shadow overflow-auto ${!isFilterOpen ? "translate-end d-none" : ""}`}
        style={{ width: "500px", zIndex: 1045 }}
      >
        <div className="d-flex flex-column h-100">
          <div className="p-3 border-bottom bg-light">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0 text-primary">
                <i className="bi bi-funnel-fill me-2"></i>Filter Phone Numbers
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setIsFilterOpen(false)}
                aria-label="Close"
              />
            </div>
          </div>

          <div className="p-3 flex-grow-1">
            {/* Phone Numbers */}
            <div className="mb-4">
              <label className="form-label fw-bold small text-muted">Phone Numbers</label>
              <div className="input-group">
                <span className="input-group-text"><i className="bi bi-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by phone numbers..."
                  name="phoneNumbers"
                  value={tempFilters.phoneNumbers}
                  onChange={handleTempFilterChange}
                  disabled={loading}
                  autoComplete="new-password"
                />
              </div>
            </div>

            {/* Full Name */}
            <div className="mb-4">
              <label className="form-label fw-bold small text-muted">Full Name</label>
              <div className="input-group">
                <span className="input-group-text"><i className="bi bi-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by full name..."
                  name="fullName"
                  value={tempFilters.fullName}
                  onChange={handleTempFilterChange}
                  disabled={loading}
                  autoComplete="new-password"
                />
              </div>
            </div>

            {/* Metadata */}
            <div className="mb-4">
              <label className="form-label fw-bold small text-muted">Metadata</label>
              <div className="input-group">
                <span className="input-group-text"><i className="bi bi-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by metadata..."
                  name="metadata"
                  value={tempFilters.metadata}
                  onChange={handleTempFilterChange}
                  disabled={loading}
                  autoComplete="new-password"
                />
              </div>
            </div>

            <hr className="my-4" />

            <div className="vstack gap-4">
              {/* Status */}
              <div>
                <label className="form-label fw-bold small text-muted mb-2">Status</label>
                <select
                  className="form-select"
                  name="status"
                  value={tempFilters.status}
                  onChange={handleTempFilterChange}
                  disabled={loading}
                >
                  <option value="">All</option>
                  <option value="subscribed">Subscribed</option>
                  <option value="blocked">Blocked</option>
                </select>
              </div>

              {/* Block Reason */}
              <div>
                <label className="form-label fw-bold small text-muted mb-2">Block Reason</label>
                <div className="input-group">
                  <span className="input-group-text"><i className="bi bi-search"></i></span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by block reason..."
                    name="blockReason"
                    value={tempFilters.blockReason}
                    onChange={handleTempFilterChange}
                    disabled={loading}
                    autoComplete="new-password"
                  />
                </div>
              </div>

              {/* Date Range */}
              <div>
                <label className="form-label fw-bold small text-muted mb-2">Date Range</label>
                <div className="mb-3">
                  <label className="form-label small text-muted">Created From</label>
                  <div className="input-group">
                    <span className="input-group-text"><i className="bi bi-calendar-event"></i></span>
                    <input
                      type="date"
                      className="form-control"
                      name="createdFrom"
                      value={tempFilters.createdFrom}
                      onChange={handleTempFilterChange}
                      disabled={loading}
                    />
                  </div>
                </div>
                <div>
                  <label className="form-label small text-muted">Created To</label>
                  <div className="input-group">
                    <span className="input-group-text"><i className="bi bi-calendar-event"></i></span>
                    <input
                      type="date"
                      className="form-control"
                      name="createdTo"
                      value={tempFilters.createdTo}
                      onChange={handleTempFilterChange}
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-3 border-top bg-light">
            <div className="d-grid gap-2">
              <button
                className="btn btn-outline-secondary"
                onClick={handleClearAllFilters}
                disabled={loading}
              >
                <i className="bi bi-x-circle me-1"></i>Clear All Filters
              </button>
              <button
                className="btn btn-primary"
                onClick={handleApplyFilters}
                disabled={loading}
              >
                <i className="bi bi-search me-1"></i>Search / Apply Filters
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* ==================== END FILTER SIDEBAR ==================== */}

      {loading && <div>Loading...</div>}
      {error && <div className="alert alert-danger">{error}</div>}
      {!loading && !error && (
        <>
          {phoneNumbers.length === 0 ? (
            <div className="alert alert-info">
              No phone numbers found.
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table align-middle">
                  <thead>
                    <tr>
                      <th>
                        <button
                          className="btn btn-link text-decoration-none text-dark p-0"
                          onClick={() => handleSort('createdAt')}
                        >
                          Date Added {sortBy === 'createdAt' && (sortDescending ? '↓' : '↑')}
                        </button>
                      </th>
                      <th>
                        <button
                          className="btn btn-link text-decoration-none text-dark p-0"
                          onClick={() => handleSort('number')}
                        >
                          Phone Number {sortBy === 'number' && (sortDescending ? '↓' : '↑')}
                        </button>
                      </th>
                      <th>
                        <button
                          className="btn btn-link text-decoration-none text-dark p-0"
                          onClick={() => handleSort('fullName')}
                        >
                          Full Name {sortBy === 'fullName' && (sortDescending ? '↓' : '↑')}
                        </button>
                      </th>
                      <th>
                        <button
                          className="btn btn-link text-decoration-none text-dark p-0"
                          onClick={() => handleSort('metadata')}
                        >
                          Metadata {sortBy === 'metadata' && (sortDescending ? '↓' : '↑')}
                        </button>
                      </th>
                      <th>Status</th>
                      <th>Block Reason</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {phoneNumbers.map((phoneNumber) => (
                      <tr key={phoneNumber.id}>
                        <td>
                          {new Date(phoneNumber.createdAt).toLocaleDateString()} {new Date(phoneNumber.createdAt).toLocaleTimeString()}
                        </td>
                        <td>{phoneNumber.number}</td>
                        <td>{phoneNumber.fullName || '-'}</td>
                        <td>
                          <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {phoneNumber.metadata || '-'}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <span className={`badge ${phoneNumber.isSubscribed ? 'bg-success' : 'bg-warning'}`}>
                              {phoneNumber.isSubscribed ? 'Subscribed' : 'Unsubscribed'}
                            </span>
                            {phoneNumber.isBlocked && (
                              <span className="badge bg-danger">Blocked</span>
                            )}
                          </div>
                        </td>
                        <td>{phoneNumber.blockReason || '-'}</td>
                        <td>
                          <div className="btn-group">
                            <button
                              className="btn btn-sm btn-outline-primary me-2"
                              onClick={() => handleEditPhoneNumber(phoneNumber)}
                              title="Edit Phone Number"
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button
                              className={`btn btn-sm ${phoneNumber.isBlocked ? 'btn-outline-success' : 'btn-outline-danger'}`}
                              onClick={() => handleBlockStatusChange(phoneNumber, phoneNumber.isBlocked)}
                              title={phoneNumber.isBlocked ? 'Unblock' : 'Block'}
                            >
                              <i className={`bi ${phoneNumber.isBlocked ? 'bi-unlock' : 'bi-shield-x'}`}></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="d-flex align-items-center">
                  <select
                    className="form-select form-select-sm me-2"
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    style={{ width: 'auto' }}
                  >
                    <option value={5}>5 per page</option>
                    <option value={10}>10 per page</option>
                    <option value={20}>20 per page</option>
                    <option value={50}>50 per page</option>
                  </select>
                  <span className="text-muted">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalItems)} of {totalItems} entries
                  </span>
                </div>
                  <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
              </div>
            </>
          )}
        </>
      )}

      {/* Floating Action Buttons */}
      <div className="position-fixed" style={{ bottom: '2rem', right: '2rem', zIndex: 1040 }}>
        <div className="position-relative">
          <button
            className="btn btn-primary rounded-circle"
            style={{
              width: '3.5rem',
              height: '3.5rem',
              boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
            }}
            onClick={() => setShowCreateDropdown(!showCreateDropdown)}
            title="Create new phone number"
          >
            <i className="bi bi-plus-lg fs-4"></i>
          </button>
          
          {showCreateDropdown && (
            <div
              className="position-absolute bottom-100 end-0 mb-2 bg-white rounded shadow-lg"
              style={{ width: '300px' }}
            >
              <div className="p-2">
                <button
                  className="btn btn-light w-100 text-start mb-2"
                  onClick={() => {
                    setShowCreateDropdown(false);
                    setNewPhoneNumberModalOpen(true);
                  }}
                >
                  <i className="bi bi-person-plus me-2"></i>
                  Create new phone number
                </button>
                <button
                  className="btn btn-light w-100 text-start mb-2"
                  onClick={() => {
                    setShowCreateDropdown(false);
                    setBulkPhoneNumbersModalOpen(true);
                  }}
                >
                  <i className="bi bi-list-ul me-2"></i>
                  Bulk import (text)
                </button>
                <button
                  className="btn btn-light w-100 text-start"
                  onClick={() => {
                    setShowCreateDropdown(false);
                    fileInputRef.current?.click();
                  }}
                >
                  <i className="bi bi-upload me-2"></i>
                  Upload phone numbers (CSV)
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Phone Number Modal */}
      {newPhoneNumberModalOpen && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create New Phone Number</h5>
                <button type="button" className="btn-close" onClick={() => setNewPhoneNumberModalOpen(false)}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleNewPhoneNumberSubmit}>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Phone Number *</label>
                    <input
                      type="tel"
                      className="form-control"
                      value={newPhoneNumber}
                      onChange={(e) => setNewPhoneNumber(e.target.value)}
                      placeholder="+1234567890"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Full Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={newFullName}
                      onChange={(e) => setNewFullName(e.target.value)}
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Metadata</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={newMetadata}
                      onChange={(e) => setNewMetadata(e.target.value)}
                      placeholder="Additional information..."
                    ></textarea>
                  </div>
                  {createError && (
                    <div className="alert alert-danger mb-3">
                      {createError}
                    </div>
                  )}
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setNewPhoneNumberModalOpen(false)}
                      disabled={isCreating}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isCreating}
                    >
                      {isCreating ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Creating...
                        </>
                      ) : (
                        'Create Phone Number'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Phone Numbers Modal */}
      {bulkPhoneNumbersModalOpen && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Bulk Import Phone Numbers</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setBulkPhoneNumbersModalOpen(false)}
                ></button>
              </div>
              <form onSubmit={handleBulkPhoneNumbersSubmit}>
                <div className="modal-body">
                  <div className="alert alert-info">
                    <i className="bi bi-info-circle me-2"></i>
                    Enter one phone number per line. Each phone number should be in international format (e.g., +1234567890).
                  </div>
                  <div className="mb-3">
                    <label htmlFor="bulkPhoneNumbers" className="form-label fw-semibold">Phone Numbers *</label>
                    <textarea
                      className="form-control"
                      id="bulkPhoneNumbers"
                      rows="10"
                      value={bulkPhoneNumbersList}
                      onChange={(e) => setBulkPhoneNumbersList(e.target.value)}
                      placeholder={"+1234567890\n+0987654321\n+1122334455"}
                      required
                    ></textarea>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setBulkPhoneNumbersModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Import Phone Numbers
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Phone Number Modal */}
      {editPhoneNumberModalOpen && selectedPhoneNumber && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Phone Number</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setEditPhoneNumberModalOpen(false)}
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleEditSubmit}>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Phone Number *</label>
                    <input
                      type="tel"
                      className="form-control"
                      value={editPhoneNumber}
                      onChange={(e) => setEditPhoneNumber(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Full Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editFullName}
                      onChange={(e) => setEditFullName(e.target.value)}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Metadata</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={editMetadata}
                      onChange={(e) => setEditMetadata(e.target.value)}
                    ></textarea>
                  </div>
                  {updateError && (
                    <div className="alert alert-danger mb-3">
                      {updateError}
                    </div>
                  )}
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setEditPhoneNumberModalOpen(false)}
                      disabled={isUpdating}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isUpdating}
                    >
                      {isUpdating ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Updating...
                        </>
                      ) : (
                        'Update Phone Number'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSV Preview Modal */}
      {csvPreviewModalOpen && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Preview Phone Numbers</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setCsvPreviewModalOpen(false);
                    setCsvPreviewData([]);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info mb-3">
                  <i className="bi bi-info-circle me-2"></i>
                  Review and edit the phone numbers before saving. You can remove rows by clicking the delete button.
                </div>
                <div className="table-responsive" style={{ maxHeight: '60vh' }}>
                  <table className="table table-hover">
                    <thead className="sticky-top bg-white">
                      <tr>
                        <th>Full Name</th>
                        <th>Phone Number</th>
                        <th>Metadata</th>
                        <th style={{ width: '50px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvPreviewData.map((row, index) => (
                        <tr key={index}>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={row.fullName}
                              onChange={(e) => handleCsvDataChange(index, 'fullName', e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="tel"
                              className="form-control form-control-sm"
                              value={row.phoneNumber}
                              onChange={(e) => handleCsvDataChange(index, 'phoneNumber', e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={row.metadata}
                              onChange={(e) => handleCsvDataChange(index, 'metadata', e.target.value)}
                            />
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleRemoveRow(index)}
                              title="Remove row"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {csvPreviewData.length === 0 && (
                  <div className="text-center text-muted py-4">
                    No phone numbers to preview
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setCsvPreviewModalOpen(false);
                    setCsvPreviewData([]);
                  }}
                  disabled={isSavingBulk}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveBulkPhoneNumbers}
                  disabled={isSavingBulk || csvPreviewData.length === 0}
                >
                  {isSavingBulk ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-save me-2"></i>
                      Save {csvPreviewData.length} Phone Numbers
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hidden file input for CSV upload */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".csv"
        onChange={handleFileUpload}
      />

    </div>
  );
}

export default PhoneNumbers;